import { useMemo, useEffect, useState } from 'react';
import { ContractTransaction, ethers } from 'ethers';
import { useUser } from 'structure/user/UserContext';
import { useBlockNumber, useConnection } from 'wagmi';
import { useReadContracts } from 'wagmi';
import { Abi } from 'viem';
import { executeContractMethod } from 'utils/ethereum';
import SDAOlaunchpadClaimAbi from 'utils/constants/abi/SDAOlaunchpadClaim';
import IUniswapV2ERC20 from '@uniswap/v2-core/build/IUniswapV2ERC20.json';
import { InsufficientBalanceError } from 'utils/errors';
import { launchPadFormatDate } from 'utils/formateDate';
import { toFraction } from 'utils/balance';
import { NetworkChainIds } from '@dapp/multi-lib/network';
import { useQueryClient } from '@tanstack/react-query';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';

type ClaimDataType = {
  launchPadClaimcontract: ethers.Contract | null;
  userInfo: {
    allocated: string;
    claimed: string;
  };
  poolInfo: {
    allocatedAmount: string;
    emissionPeriod: {
      start: string;
      end: string;
    };
  };
  claimableAmount: string;
  claim: () => Promise<ContractTransaction>;
};

export function useSDAOLaunchpadClaim(
  launchpadClaimContractAddress: string,
  poolId: number,
  targetChainId?: NetworkChainIds
): {
  data: ClaimDataType | undefined;
  loading: boolean;
  error: string | undefined;
  refetch: () => Promise<void>;
} {
  const { account, library } = useUser();
  const { address: wagmiAddress } = useConnection();
  const [tokenDecimals, setTokenDecimals] = useState<number | null>(null);
  const [tokenAddress, setTokenAddress] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>(undefined);

  // Use targetChainId if provided, otherwise use current chain from useUser
  const chainId = targetChainId || undefined;
  const address = wagmiAddress || account;

  // Create contract calls for reading data
  const contracts = useMemo(() => {
    if (!launchpadClaimContractAddress) return [];

    const contractCalls = [
      {
        address: launchpadClaimContractAddress as `0x${string}`,
        abi: SDAOlaunchpadClaimAbi,
        functionName: 'poolInfo',
        args: [poolId],
        chainId: chainId as number | undefined,
      },
    ];

    // Only add user-specific calls if we have an address
    if (address) {
      contractCalls.push(
        {
          address: launchpadClaimContractAddress as `0x${string}`,
          abi: SDAOlaunchpadClaimAbi,
          functionName: 'userInfo',
          args: [poolId, address],
          chainId: chainId as number | undefined,
        },
        {
          address: launchpadClaimContractAddress as `0x${string}`,
          abi: SDAOlaunchpadClaimAbi,
          functionName: 'claimableTokens',
          args: [poolId, address],
          chainId: chainId as number | undefined,
        }
      );
    }

    return contractCalls;
  }, [launchpadClaimContractAddress, poolId, address, chainId]);

  // Read contract data using wagmi
  const {
    queryKey: contractDataQueryKey,
    data: contractData,
    isLoading,
    error: contractError,
    refetch: refetchContracts,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
  } = useReadContracts({
    query: {
      enabled: contracts.length > 0 && !!launchpadClaimContractAddress,
    },
    contracts: contracts,
  });

  useEffect(() => {
    if (contractError) {
      setError(contractError?.message || 'Failed to read contract data');
    }
  }, [contractError]);

  const queryClient = useQueryClient();
  const { data: blockNumber } = useBlockNumber({
    watch: true,
    cacheTime: ETH_AVERAGE_BLOCK_TIME * 5,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: contractDataQueryKey });
  }, [blockNumber, queryClient]);

  // Read token decimals when we have the token address
  const { data: decimalsData, isLoading: decimalsLoading } = useReadContracts({
    contracts: tokenAddress
      ? [
          {
            address: tokenAddress as `0x${string}`,
            abi: IUniswapV2ERC20.abi as Abi,
            functionName: 'decimals',
            chainId: chainId as number | undefined,
          },
        ]
      : [],
    query: {
      enabled: !!tokenAddress,
    },
  });

  // Extract token address from poolInfo
  useEffect(() => {
    if (
      contractData &&
      contractData[0] &&
      contractData[0].status === 'success'
    ) {
      const poolInfo = contractData[0].result as unknown as readonly unknown[];
      // poolInfo[0] is the claimToken address
      if (poolInfo && poolInfo[0]) {
        setTokenAddress((poolInfo[0] as `0x${string}`).toString());
      }
    }
  }, [contractData]);

  // Extract token decimals
  useEffect(() => {
    if (
      decimalsData &&
      decimalsData[0] &&
      decimalsData[0].status === 'success'
    ) {
      setTokenDecimals(Number(decimalsData[0].result));
    }
  }, [decimalsData]);

  // Transform contract data to match the expected format
  const data = useMemo((): ClaimDataType | undefined => {
    if (!contractData || contractData.length === 0) return undefined;
    if (contractError) return undefined;

    const poolInfoResult = contractData[0];
    // Check status instead of error property
    if (poolInfoResult?.status !== 'success' || !poolInfoResult?.result) {
      return undefined;
    }

    // poolInfo is a tuple: [claimToken, allocatedAmount, emissionPeriod, instantUnlockRatio, totalClaimed]
    // emissionPeriod is a tuple: [startOfEmissions, endOfVestingCliff, endOfEmissions, vestingCliffAccrues]
    const poolInfo = poolInfoResult.result as unknown as readonly unknown[];

    // Extract values from array indices
    const allocatedAmount = poolInfo?.[1] as bigint | undefined;
    const emissionPeriod = poolInfo?.[2] as
      | (readonly unknown[] & {
          startOfEmissions?: unknown;
          endOfEmissions?: unknown;
        })
      | undefined; // This is a tuple

    // Wagmi might return tuples as arrays or objects, handle both cases
    // The ABI has named fields, so wagmi might return as object with properties
    let startOfEmissions;
    let endOfEmissions;

    if (emissionPeriod) {
      if (Array.isArray(emissionPeriod)) {
        // Array format: [startOfEmissions, endOfVestingCliff, endOfEmissions, vestingCliffAccrues]
        startOfEmissions = emissionPeriod[0];
        endOfEmissions = emissionPeriod[2]; // Note: index 2, not 1 (endOfVestingCliff is at index 1)
      } else if (typeof emissionPeriod === 'object') {
        // Object format with named properties (wagmi returns structs as objects when ABI has names)
        startOfEmissions = emissionPeriod.startOfEmissions ?? emissionPeriod[0];
        endOfEmissions = emissionPeriod.endOfEmissions ?? emissionPeriod[2];
      }
    }

    // Convert to number safely (handle BigInt, string, or number)
    const convertToNumber = (value: unknown): number => {
      if (value === null || value === undefined) return 0;
      if (typeof value === 'bigint') return Number(value);
      if (typeof value === 'string') {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
      }
      if (typeof value === 'number') return value;
      return 0;
    };

    const startTimestamp = convertToNumber(startOfEmissions);
    const endTimestamp = convertToNumber(endOfEmissions);

    // If no account, return data with zero claimable
    if (!address) {
      return {
        launchPadClaimcontract: null, // We'll create this when needed for claiming
        userInfo: {
          allocated: '0',
          claimed: '0',
        },
        poolInfo: {
          allocatedAmount: allocatedAmount?.toString() || '0',
          emissionPeriod: {
            start:
              startTimestamp > 0
                ? launchPadFormatDate(new Date(startTimestamp * 1000))
                : '---',
            end:
              endTimestamp > 0
                ? launchPadFormatDate(new Date(endTimestamp * 1000))
                : '---',
          },
        },
        claimableAmount: '0',
        claim: async () => {
          throw new Error('Account required to claim');
        },
      };
    }

    // Process user-specific data (only if we have an address)
    const userInfoResult = contractData[1];
    const claimableTokensResult = contractData[2];

    if (userInfoResult?.status !== 'success' || !userInfoResult?.result) {
      return undefined;
    }
    if (
      claimableTokensResult?.status !== 'success' ||
      !claimableTokensResult?.result
    ) {
      return undefined;
    }

    // userInfo is a tuple: [allocated, claimed]
    const userInfo = userInfoResult.result as unknown as readonly unknown[];
    const userAllocated = userInfo?.[0] as bigint | undefined;
    const userClaimed = userInfo?.[1] as bigint | undefined;
    const claimableAmount = claimableTokensResult.result;
    const decimals = tokenDecimals || 18; // Default to 18 if not loaded yet

    // Create ethers contract instance for claiming (will use library when needed)
    let launchPadClaimcontract: ethers.Contract | null = null;
    if (library) {
      launchPadClaimcontract = new ethers.Contract(
        launchpadClaimContractAddress,
        SDAOlaunchpadClaimAbi,
        library
      );
    }

    return {
      launchPadClaimcontract: launchPadClaimcontract,
      userInfo: {
        allocated: toFraction(
          userAllocated?.toString() || '0',
          decimals,
          Infinity
        ),
        claimed: toFraction(userClaimed?.toString() || '0', decimals, Infinity),
      },
      poolInfo: {
        allocatedAmount: toFraction(
          allocatedAmount?.toString() || '0',
          decimals,
          Infinity
        ),
        emissionPeriod: {
          start:
            startTimestamp > 0
              ? launchPadFormatDate(new Date(startTimestamp * 1000))
              : '---',
          end:
            endTimestamp > 0
              ? launchPadFormatDate(new Date(endTimestamp * 1000))
              : '---',
        },
      },
      claimableAmount: toFraction(
        claimableAmount?.toString() || '0',
        decimals,
        Infinity
      ),

      claim: async (): Promise<ContractTransaction> => {
        const claimableBigInt = BigInt(claimableAmount?.toString() || '0');
        if (!claimableAmount || claimableBigInt === BigInt(0)) {
          throw new InsufficientBalanceError('There is no amount to claim');
        }
        // For claiming, we need the signer from the current library (user's wallet)
        if (!library) {
          throw new Error('Library not available for claiming');
        }
        const signer = await library.getSigner();
        const claimContract = new ethers.Contract(
          launchpadClaimContractAddress,
          SDAOlaunchpadClaimAbi,
          signer
        );
        const gasPrice = await signer.getGasPrice();
        return executeContractMethod(
          claimContract,
          'claim',
          gasPrice,
          poolId.toString(),
          address
        ) as unknown as ContractTransaction;
      },
    };
  }, [
    contractData,
    contractError,
    tokenDecimals,
    address,
    library,
    launchpadClaimContractAddress,
    poolId,
  ]);

  // Determine loading state
  const loading = isLoading || (!!tokenAddress && decimalsLoading);

  // Refetch function
  const refetch = async () => {
    setError(undefined);
    await refetchContracts();
  };

  return {
    data,
    loading,
    error: error || contractError?.message,
    refetch,
  };
}
